import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Image, Upload, Trash2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function LogoConfigPage() {
  const { api } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await api.get('/settings/logo');
      setLogoUrl(response.data.logo_url);
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLogoUrl(response.data.logo_url);
      toast.success('Logo atualizada!');
      
      // Forçar reload para atualizar o sidebar
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!confirm('Remover logo personalizada e voltar ao padrão?')) return;

    setUploading(true);
    try {
      await api.delete('/admin/logo');
      setLogoUrl(null);
      toast.success('Logo removida!');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Image className="w-8 h-8 text-primary" />
          Logo do Sistema
        </h1>
        <p className="text-muted-foreground">Personalize a logo exibida no menu lateral</p>
      </div>

      {/* Status */}
      <div className={`flex items-center gap-2 p-3 rounded-lg ${logoUrl ? 'bg-green-500/10 text-green-500' : 'bg-muted text-muted-foreground'}`}>
        {logoUrl ? (
          <>
            <CheckCircle2 className="w-5 h-5" />
            <span>Logo personalizada ativa</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-5 h-5" />
            <span>Usando logo padrão do sistema</span>
          </>
        )}
      </div>

      {/* Preview */}
      <Card className="p-6">
        <h3 className="font-bold mb-4">Preview</h3>
        <div className="flex items-center justify-center p-8 bg-sidebar rounded-lg border-2 border-dashed">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="max-h-12 max-w-[180px] object-contain"
            />
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold">N</span>
              </div>
              <div>
                <h1 className="font-bold text-xl">NEXUS</h1>
                <p className="text-[10px] font-mono text-primary uppercase">WhatsApp</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Upload */}
      <Card className="p-6">
        <h3 className="font-bold mb-4">Upload de Logo</h3>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {logoUrl ? 'Trocar Logo' : 'Enviar Logo'}
          </Button>
          
          {logoUrl && (
            <Button 
              variant="outline" 
              onClick={handleRemoveLogo}
              disabled={uploading}
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remover
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Formatos aceitos: PNG, JPG, SVG, WebP. Tamanho máximo: 2MB.<br />
          Recomendado: Imagem com fundo transparente, altura de 40-48px.
        </p>
      </Card>

      {/* Dicas */}
      <Card className="p-6 border-dashed">
        <h3 className="font-bold mb-3">💡 Dicas</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Use imagens com fundo transparente (PNG ou SVG)</li>
          <li>• A logo será redimensionada para no máximo 180x48 pixels</li>
          <li>• Para melhor resultado, use uma imagem horizontal (logo deitada)</li>
          <li>• Após trocar a logo, a página será recarregada automaticamente</li>
        </ul>
      </Card>
    </div>
  );
}
